// Page de prise de rendez-vous pour vérification manuelle
// AbracadaBati - Créneaux 8h-20h, 7j/7, toutes les 5 minutes

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Clock, Video, ChevronLeft, ChevronRight, CheckCircle,
  Phone, User, ArrowLeft, X, Shield, AlertCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Jours en français
const DAYS_FR = {
  'Monday': 'Lundi',
  'Tuesday': 'Mardi',
  'Wednesday': 'Mercredi',
  'Thursday': 'Jeudi',
  'Friday': 'Vendredi',
  'Saturday': 'Samedi',
  'Sunday': 'Dimanche'
};

const MONTHS_FR = {
  'January': 'Janvier',
  'February': 'Février',
  'March': 'Mars',
  'April': 'Avril',
  'May': 'Mai',
  'June': 'Juin',
  'July': 'Juillet',
  'August': 'Août',
  'September': 'Septembre',
  'October': 'Octobre',
  'November': 'Novembre',
  'December': 'Décembre'
};

export default function VerificationAppointmentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [availableSlots, setAvailableSlots] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [existingAppointment, setExistingAppointment] = useState(null);
  const [confirmationStep, setConfirmationStep] = useState(false);
  
  const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('buyer_token');

  // Charger les créneaux disponibles
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Vérifier si l'utilisateur a déjà un RDV
        const myAppointmentRes = await fetch(`${API_URL}/api/verification-appointments/my-appointment`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (myAppointmentRes.ok) {
          const myAppointmentData = await myAppointmentRes.json();
          if (myAppointmentData.has_appointment) {
            setExistingAppointment(myAppointmentData.appointment);
            setLoading(false);
            return;
          }
        }
        
        // Charger les créneaux
        const slotsRes = await fetch(`${API_URL}/api/verification-appointments/available-slots?days=14`);
        if (slotsRes.ok) {
          const slotsData = await slotsRes.json();
          setAvailableSlots(slotsData.available_slots);
          
          // Sélectionner le premier jour par défaut
          const firstDate = Object.keys(slotsData.available_slots)[0];
          if (firstDate) {
            setSelectedDate(firstDate);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  // Réserver le créneau
  const bookAppointment = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Sélectionne une date et une heure');
      return;
    }
    
    setBooking(true);
    
    try {
      const res = await fetch(`${API_URL}/api/verification-appointments/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: selectedDate,
          time: selectedTime
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setExistingAppointment(data.appointment);
        setConfirmationStep(false);
        toast.success('Rendez-vous confirmé !');
      } else {
        toast.error(data.detail || 'Erreur lors de la réservation');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setBooking(false);
    }
  };

  // Annuler le RDV
  const cancelAppointment = async () => {
    if (!confirm('Es-tu sûr de vouloir annuler ton rendez-vous ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/verification-appointments/cancel`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        setExistingAppointment(null);
        toast.success('Rendez-vous annulé');
        
        // Recharger les créneaux
        const slotsRes = await fetch(`${API_URL}/api/verification-appointments/available-slots?days=14`);
        if (slotsRes.ok) {
          const slotsData = await slotsRes.json();
          setAvailableSlots(slotsData.available_slots);
          const firstDate = Object.keys(slotsData.available_slots)[0];
          if (firstDate) setSelectedDate(firstDate);
        }
      } else {
        toast.error('Erreur lors de l\'annulation');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    }
  };

  // Formater la date pour affichage
  const formatDate = (dateStr) => {
    const slot = availableSlots[dateStr];
    if (!slot) return dateStr;
    return `${DAYS_FR[slot.day_name] || slot.day_name} ${slot.day_number} ${MONTHS_FR[slot.month] || slot.month}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Afficher le RDV existant
  if (existingAppointment) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-gray-100">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-xl">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <Calendar size={20} className="text-blue-600" />
              <span className="font-semibold text-gray-900">Mon rendez-vous</span>
            </div>
            <div className="w-10" />
          </div>
        </div>

        <div className="max-w-md mx-auto px-4 py-8">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-6 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} />
            </div>
            <h2 className="text-xl font-bold mb-2">Rendez-vous confirmé</h2>
            <p className="text-emerald-100 mb-6">Notre équipe t'appellera en visio</p>
            
            <div className="bg-white/10 rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Calendar size={20} />
                <span className="font-medium">{formatDate(existingAppointment.date)}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock size={20} />
                <span className="font-medium">{existingAppointment.time}</span>
              </div>
              <div className="flex items-center gap-3">
                <Video size={20} />
                <span className="text-sm">Appel vidéo via Abracadachat</span>
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
              <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <Shield size={18} />
                Comment ça se passe ?
              </h3>
              <ul className="text-sm text-blue-700 space-y-2">
                <li>• Tu recevras un appel vidéo à l'heure prévue</li>
                <li>• Prépare ta pièce d'identité</li>
                <li>• L'appel dure moins d'une minute</li>
                <li>• Tu seras vérifié immédiatement après</li>
              </ul>
            </div>

            <Button
              onClick={cancelAppointment}
              variant="outline"
              className="w-full h-12 rounded-xl text-gray-600"
            >
              <X size={18} className="mr-2" />
              Annuler le rendez-vous
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-blue-600" />
            <span className="font-semibold text-gray-900">Prendre rendez-vous</span>
          </div>
          <div className="w-10" />
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 pb-32">
        {/* Intro */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Video size={28} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Appel de vérification</h1>
          <p className="text-gray-500 text-sm mt-2">
            Un appel vidéo de 1 minute avec notre équipe pour finaliser ta vérification
          </p>
        </div>

        {/* Date selection */}
        <div className="mb-6">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Calendar size={18} className="text-blue-600" />
            Choisis une date
          </h3>
          
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {Object.keys(availableSlots).map((date) => {
              const slot = availableSlots[date];
              const isSelected = selectedDate === date;
              
              return (
                <button
                  key={date}
                  onClick={() => {
                    setSelectedDate(date);
                    setSelectedTime(null);
                  }}
                  className={`flex-shrink-0 p-3 rounded-2xl text-center transition-all min-w-[80px] ${
                    isSelected
                      ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg'
                      : 'bg-white border border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <p className={`text-xs ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                    {DAYS_FR[slot.day_name]?.substring(0, 3) || slot.day_short}
                  </p>
                  <p className={`text-lg font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                    {slot.day_number}
                  </p>
                  <p className={`text-xs ${isSelected ? 'text-blue-100' : 'text-gray-400'}`}>
                    {slot.slots_count} dispo
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Time selection */}
        {selectedDate && availableSlots[selectedDate] && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock size={18} className="text-blue-600" />
              Choisis une heure
            </h3>
            
            <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto">
              {availableSlots[selectedDate].slots.map((time) => {
                const isSelected = selectedTime === time;
                
                return (
                  <button
                    key={time}
                    onClick={() => setSelectedTime(time)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg'
                        : 'bg-white border border-gray-200 hover:border-blue-300 text-gray-700'
                    }`}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Confirmation step */}
        {selectedDate && selectedTime && !confirmationStep && (
          <Button
            onClick={() => setConfirmationStep(true)}
            className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-semibold shadow-lg"
          >
            Confirmer le créneau
          </Button>
        )}

        {/* Confirmation modal */}
        <AnimatePresence>
          {confirmationStep && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4"
              onClick={() => setConfirmationStep(false)}
            >
              <motion.div
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                exit={{ y: 100 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl p-6 w-full max-w-md"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
                  Confirmer le rendez-vous ?
                </h3>
                
                <div className="bg-gray-50 rounded-2xl p-4 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar size={20} className="text-blue-600" />
                    <span className="font-medium">{formatDate(selectedDate)}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock size={20} className="text-blue-600" />
                    <span className="font-medium">{selectedTime}</span>
                  </div>
                </div>
                
                <div className="bg-amber-50 rounded-xl p-3 mb-6 border border-amber-200">
                  <p className="text-xs text-amber-700 text-center flex items-center justify-center gap-2">
                    <AlertCircle size={14} />
                    Sois disponible à l'heure choisie pour l'appel vidéo
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => setConfirmationStep(false)}
                    variant="outline"
                    className="flex-1 h-12 rounded-xl"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={bookAppointment}
                    disabled={booking}
                    className="flex-1 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl"
                  >
                    {booking ? 'Réservation...' : 'Confirmer'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
